import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ChartBarIcon, 
  CogIcon, 
  ClockIcon, 
  CheckCircleIcon,
  ArrowRightIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

const HomePage: React.FC = () => {
  const features = [
    {
      icon: ChartBarIcon,
      title: 'Assessment AI Readiness',
      description: 'Valutazione personalizzata del livello di preparazione della tua azienda per l\'AI'
    },
    {
      icon: SparklesIcon,
      title: 'Raccomandazioni Intelligenti',
      description: 'Suggerimenti specifici sui primi AI Agent da implementare basati sul tuo business'
    },
    {
      icon: ClockIcon,
      title: 'Roadmap 30-60-90 Giorni',
      description: 'Timeline dettagliata con task specifici e milestone concreti'
    },
    {
      icon: CogIcon,
      title: 'Risorse specializzate',
      description: 'Assistenza da parte di esperti in settore per aiutarti a implementare l\'AI Agent'
    }
  ];

  const benefits = [
    'Time to value di 120-180 giorni',
    'Riduzione dei costi operativi fino al 30%',
    'Miglioramento customer satisfaction del 40%'
  ];

  const useCases = [
    {
      title: 'Personal Shopping Assistant',
      description: 'AI che guida i clienti nella scelta dei prodotti personalizzati',
      roi: '300-500%',
      sector: 'Fashion & Beauty'
    },
    {
      title: 'Customer Service Bot',
      description: 'Assistenza clienti 24/7 con escalation intelligente',
      roi: '200-350%',
      sector: 'Tutti i settori'
    },
    {
      title: 'Gestione Resi Automatica',
      description: 'Processo di reso semplificato con approvazione automatica',
      roi: '150-250%',
      sector: 'E-commerce'
    }
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl">
              Implementa AI Agents nel tuo{' '}
              <span className="text-primary-600">e-commerce</span>
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-xl text-gray-500">
              Ti guidiamo nell'identificazione, pianificazione e implementazione del primo AI Agent 
              per la tua azienda con un assessment personalizzato e una roadmap concreta.
            </p>
            <div className="mt-10 flex items-center justify-center gap-x-6">
              <Link
                to="/assessment"
                className="rounded-md bg-primary-600 px-6 py-3 text-lg font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 transition-colors"
              >
                Inizia Assessment Gratuito
              </Link>
              <button className="text-lg font-semibold leading-6 text-gray-900 hover:text-primary-600 transition-colors">
                Scopri di più <ArrowRightIcon className="inline h-5 w-5 ml-1" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-24 bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Come funziona l'AI Implementation Advisor
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-xl text-gray-500">
              Un processo guidato in 4 step per portare l'AI nella tua azienda
            </p>
          </div>

          <div className="mt-20 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((feature, index) => (
              <div key={index} className="text-center">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-lg bg-primary-600">
                  <feature.icon className="h-8 w-8 text-white" aria-hidden="true" />
                </div>
                <h3 className="mt-6 text-lg font-semibold text-gray-900">{feature.title}</h3>
                <p className="mt-2 text-base text-gray-500">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Use Cases Section */}
      <div className="py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              I primi AI Agent da implementare
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-xl text-gray-500">
              Casi d'uso testati con ROI comprovato dalle migliori aziende italiane
            </p>
          </div>

          <div className="mt-20 grid grid-cols-1 gap-8 lg:grid-cols-3">
            {useCases.map((useCase, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg p-8 border border-gray-200 hover:shadow-xl transition-shadow">
                <div className="text-sm font-semibold text-primary-600 uppercase tracking-wide">
                  {useCase.sector}
                </div>
                <h3 className="mt-2 text-xl font-bold text-gray-900">{useCase.title}</h3>
                <p className="mt-4 text-gray-500">{useCase.description}</p>
                <div className="mt-6 flex items-center justify-between">
                  <span className="text-sm text-gray-500">ROI Stimato</span>
                  <span className="text-lg font-bold text-green-600">{useCase.roi}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="bg-primary-600 py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Perché scegliere AI Implementation Advisor
            </h2>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <CheckCircleIcon className="mx-auto h-8 w-8 text-white mb-4" />
                <p className="text-lg text-white font-medium">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="bg-gray-50 rounded-2xl p-8 lg:p-16 text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Pronto a iniziare il tuo percorso nell'AI?
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-xl text-gray-500">
              Completa il nostro assessment gratuito in meno di 10 minuti e ricevi 
              una roadmap personalizzata per implementare il tuo primo AI Agent.
            </p>
            <div className="mt-10">
              <Link
                to="/assessment"
                className="rounded-md bg-primary-600 px-8 py-4 text-lg font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 transition-colors"
              >
                Inizia Assessment Gratuito
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;